/**
 * ErrorConsole — modal Admin para monitorizar erros do backend/web/mobile.
 *
 * Endpoints:
 *   GET  /api/admin/errors            (lista com filtros)
 *   GET  /api/admin/errors/stats      (contagens)
 *   POST /api/admin/errors/{id}/resolve
 *   POST /api/admin/errors/bulk-resolve
 *
 * Feature-flags:
 *   - Filtros por source, level, resolved, categoria, search em mensagem
 *   - Expandir stack trace ao clicar
 *   - Botões: Resolver / Bulk resolver / Refresh
 *   - Contagens de resumo no topo (total / unresolved / last24h / by_source)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_COLORS = {
  backend: { bg: '#8B4444', fg: '#F2EBDD' },
  web:     { bg: '#4A6FA5', fg: '#F2EBDD' },
  mobile:  { bg: '#5A7A3A', fg: '#F2EBDD' },
};
const LEVEL_COLORS = {
  error: '#D67373',
  warn:  '#C4A275',
  info:  '#7D95C4',
};

function _fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso.slice(0, 19); }
}

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    style={{
      padding: '4px 10px',
      borderRadius: 4,
      border: `1px solid ${active ? '#E87A2A' : '#3D362E'}`,
      background: active ? '#E87A2A' : 'transparent',
      color: active ? '#0F0D0B' : '#C4A275',
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 1,
      fontFamily: "'JetBrains Mono', monospace",
      textTransform: 'uppercase',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

export default function ErrorConsole({ open, onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterSource, setFilterSource] = useState(null);   // backend | web | mobile
  const [filterLevel, setFilterLevel] = useState(null);
  const [filterResolved, setFilterResolved] = useState(false); // por defeito: apenas não-resolvidos
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);            // error_id expandido
  const [selected, setSelected] = useState(new Set());
  const [analyzing, setAnalyzing] = useState(null);          // error_id em análise IA
  const [busy, setBusy] = useState(false);
  // Set/2026 v16v-fix8 — Trace Mode (breadcrumbs mobile ponta-a-ponta)
  const [traceStatus, setTraceStatus] = useState(null); // { active, expires_at, traces_stored }
  const [traceMinutes, setTraceMinutes] = useState(15);
  const [traceBusy, setTraceBusy] = useState(false);

  const loadTraceStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/trace/status`, { withCredentials: true });
      setTraceStatus(r.data);
    } catch { setTraceStatus(null); }
  }, []);

  const startTrace = async () => {
    setTraceBusy(true);
    try {
      const r = await axios.post(`${API}/admin/trace/start?minutes=${traceMinutes}`,
        {}, { withCredentials: true });
      setTraceStatus({ active: true, expires_at: r.data.expires_at, traces_stored: 0 });
      toast.success(`🔴 Trace ligado por ${traceMinutes}min. Relança a app no telemóvel.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao iniciar trace');
    } finally { setTraceBusy(false); }
  };

  const stopTrace = async () => {
    setTraceBusy(true);
    try {
      await axios.post(`${API}/admin/trace/stop`, {}, { withCredentials: true });
      await loadTraceStatus();
      toast.success('Trace desligado');
    } catch (err) {
      toast.error('Falha ao parar trace');
    } finally { setTraceBusy(false); }
  };

  const clearTraces = async () => {
    setTraceBusy(true);
    try {
      const r = await axios.post(`${API}/admin/trace/clear`, {}, { withCredentials: true });
      await loadTraceStatus(); await load();
      toast.success(`${r.data.removed} traces apagados`);
    } catch (err) {
      toast.error('Falha ao limpar traces');
    } finally { setTraceBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, offset: 0 };
      if (filterSource) params.source = filterSource;
      if (filterLevel) params.level = filterLevel;
      if (filterResolved !== null) params.resolved = filterResolved;
      if (search.trim()) params.search = search.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/errors`, { params, withCredentials: true }),
        axios.get(`${API}/admin/errors/stats`, { withCredentials: true }),
      ]);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data || null);
      setSelected(new Set());
    } catch (err) {
      toast.error('Falha ao carregar erros');
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterLevel, filterResolved, search]);

  useEffect(() => {
    if (open) { load(); loadTraceStatus(); }
  }, [open, load, loadTraceStatus]);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const resolveOne = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/errors/${id}/resolve`, {}, { withCredentials: true });
      toast.success('Erro marcado como resolvido');
      await load();
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error('Falha ao resolver');
    } finally { setBusy(false); }
  };

  const bulkResolve = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/errors/bulk-resolve`,
        { error_ids: Array.from(selected) }, { withCredentials: true });
      toast.success(`${res.data.resolved} erros resolvidos`);
      await load();
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error('Falha ao resolver em massa');
    } finally { setBusy(false); }
  };

  const analyzeWithAi = async (id) => {
    setAnalyzing(id);
    try {
      const res = await axios.post(`${API}/admin/errors/${id}/analyze`,
        {}, { withCredentials: true, timeout: 60000 });
      // Atualiza inline com o ai_analysis + severity devolvidos
      setItems((prev) => prev.map((e) => e.error_id === id
        ? { ...e, ai_analysis: res.data.analysis, severity: res.data.severity,
            ai_analyzed_at: new Date().toISOString() }
        : e));
      setExpanded(id);  // Auto-expande para mostrar análise
      toast.success('🤖 Análise IA concluída');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha na análise IA');
    } finally {
      setAnalyzing(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-y-auto"
        data-testid="error-console-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: '#F2EBDD',
            }}
          >
            🐞 Error Monitor
          </DialogTitle>
          <DialogDescription>
            Erros capturados do backend, dashboard web e app mobile. Retenção 30 dias.
          </DialogDescription>
        </DialogHeader>

        {/* Trace Mode Panel — Set/2026 v16v-fix8 */}
        <div
          data-testid="trace-mode-panel"
          className="my-2 p-3 rounded"
          style={{
            background: traceStatus?.active ? '#3a1a0f' : '#151312',
            border: `1px solid ${traceStatus?.active ? '#E87A2A' : '#3D362E'}`,
          }}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div style={{
              fontSize: 11, fontWeight: 900, letterSpacing: 2,
              fontFamily: "'JetBrains Mono', monospace",
              color: traceStatus?.active ? '#E87A2A' : '#C4A275',
              textTransform: 'uppercase',
            }}>
              {traceStatus?.active ? '🔴 TRACE ACTIVO' : '⚪ TRACE MODE'}
            </div>
            {traceStatus?.active && traceStatus.expires_at && (
              <div style={{ fontSize: 10, color: '#C4A275',
                            fontFamily: "'JetBrains Mono', monospace" }}>
                expira: {_fmtDate(traceStatus.expires_at)}
              </div>
            )}
            {typeof traceStatus?.traces_stored === 'number' && (
              <div style={{ fontSize: 10, color: '#7D95C4',
                            fontFamily: "'JetBrains Mono', monospace" }}>
                traces guardados: <b>{traceStatus.traces_stored}</b>
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              {!traceStatus?.active ? (
                <>
                  <select
                    value={traceMinutes}
                    onChange={(e) => setTraceMinutes(Number(e.target.value))}
                    data-testid="trace-minutes-select"
                    style={{
                      background: '#0F0D0B', color: '#F2EBDD',
                      border: '1px solid #3D362E', padding: '4px 6px',
                      fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                      borderRadius: 3,
                    }}
                  >
                    {[5, 10, 15, 30, 60, 120].map((m) => (
                      <option key={m} value={m}>{m} min</option>
                    ))}
                  </select>
                  <Button
                    size="sm" onClick={startTrace} disabled={traceBusy}
                    data-testid="trace-start-btn"
                    style={{
                      background: '#E87A2A', color: '#0F0D0B',
                      fontSize: 10, fontWeight: 900, letterSpacing: 1,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    ▶ ACTIVAR TRACE
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm" onClick={stopTrace} disabled={traceBusy}
                    data-testid="trace-stop-btn"
                    style={{
                      background: '#8B4444', color: '#F2EBDD',
                      fontSize: 10, fontWeight: 900, letterSpacing: 1,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    ■ PARAR
                  </Button>
                </>
              )}
              <Button
                size="sm" variant="ghost" onClick={clearTraces} disabled={traceBusy}
                data-testid="trace-clear-btn"
                title="Apagar todos os registos com category=trace_request"
                style={{
                  fontSize: 10, letterSpacing: 1, color: '#7D95C4',
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                🗑 LIMPAR
              </Button>
            </div>
          </div>
          <div style={{
            fontSize: 10, color: '#7D95C4', marginTop: 6,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {traceStatus?.active
              ? 'Cada request /api/* + eventos mobile (nav.route, api.request.*, dl.progress, mf.boot) são gravados. Relança a app no telemóvel para o poll agarrar a flag.'
              : 'Activa para capturar cada request /api/* + eventos mobile ponta-a-ponta. Filtra por categoria trace_request / nav.route / etc.'}
          </div>
        </div>

        {/* Stats resumo */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-2">
            <StatCard label="Total" value={stats.total} testid="stat-total" />
            <StatCard label="Não resolvidos" value={stats.unresolved}
                      color="#D67373" testid="stat-unresolved" />
            <StatCard label="Últimas 24h" value={stats.last_24h}
                      color="#E87A2A" testid="stat-24h" />
            <StatCard label="Categorias" value={Object.keys(stats.by_category || {}).length}
                      testid="stat-categories" />
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-2 items-center my-3 pb-3 border-b border-border">
          <span className="text-xs text-muted-foreground font-mono">Fonte:</span>
          <Chip active={filterSource === null} onClick={() => setFilterSource(null)} testid="filter-source-all">Todas</Chip>
          {['backend', 'web', 'mobile'].map((s) => (
            <Chip key={s} active={filterSource === s} onClick={() => setFilterSource(s)} testid={`filter-source-${s}`}>{s}</Chip>
          ))}
          <span className="text-xs text-muted-foreground font-mono ml-2">Nível:</span>
          <Chip active={filterLevel === null} onClick={() => setFilterLevel(null)} testid="filter-level-all">Todos</Chip>
          {['error', 'warn', 'info'].map((l) => (
            <Chip key={l} active={filterLevel === l} onClick={() => setFilterLevel(l)} testid={`filter-level-${l}`}>{l}</Chip>
          ))}
          <span className="text-xs text-muted-foreground font-mono ml-2">Estado:</span>
          <Chip active={filterResolved === false} onClick={() => setFilterResolved(false)} testid="filter-unresolved">Não resolvidos</Chip>
          <Chip active={filterResolved === true} onClick={() => setFilterResolved(true)} testid="filter-resolved">Resolvidos</Chip>
          <Chip active={filterResolved === null} onClick={() => setFilterResolved(null)} testid="filter-all">Todos</Chip>
          <input
            type="text"
            placeholder="Pesquisar mensagem..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="error-search-input"
            className="text-xs px-2 py-1 border border-border rounded bg-background ml-auto"
            style={{ minWidth: 200, fontFamily: "'JetBrains Mono', monospace" }}
          />
          <Button
            size="sm" onClick={load} disabled={loading}
            data-testid="refresh-errors-btn"
            style={{ fontSize: 10, letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}
          >
            {loading ? '...' : '↻ RELOAD'}
          </Button>
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="flex items-center gap-2 mb-2 p-2 rounded bg-secondary">
            <span className="text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {selected.size} selecionado{selected.size > 1 ? 's' : ''}
            </span>
            <Button
              size="sm" onClick={bulkResolve} disabled={busy}
              data-testid="bulk-resolve-btn"
              style={{ background: '#E87A2A', color: '#0F0D0B', fontSize: 10, fontWeight: 800,
                       letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}
            >
              ✓ Resolver selecionados
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Limpar
            </Button>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">A carregar erros…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center" data-testid="errors-empty">
            <div style={{ fontSize: 48, marginBottom: 8 }}>✨</div>
            <div className="text-sm text-muted-foreground">Sem erros para mostrar. Bom sinal!</div>
          </div>
        ) : (
          <div className="space-y-1" data-testid="errors-list">
            {items.map((e) => (
              <ErrorRow
                key={e.error_id}
                err={e}
                expanded={expanded === e.error_id}
                onExpand={() => setExpanded(expanded === e.error_id ? null : e.error_id)}
                selected={selected.has(e.error_id)}
                onSelect={() => toggleSel(e.error_id)}
                onResolve={() => resolveOne(e.error_id)}
                onAnalyze={() => analyzeWithAi(e.error_id)}
                analyzing={analyzing === e.error_id}
                busy={busy}
              />
            ))}
            {total > items.length && (
              <div className="text-center text-xs text-muted-foreground py-2">
                A mostrar {items.length} de {total} · usa filtros para reduzir
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div
      className="border border-border rounded p-2 bg-card"
      data-testid={testid}
    >
      <div className="text-xs uppercase text-muted-foreground" style={{
        fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1,
      }}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 900, color: color || '#F2EBDD',
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</div>
    </div>
  );
}

function ErrorRow({ err, expanded, onExpand, selected, onSelect, onResolve, onAnalyze, analyzing, busy }) {
  const src = SOURCE_COLORS[err.source] || { bg: '#3D362E', fg: '#F2EBDD' };
  const lvl = LEVEL_COLORS[err.level] || '#F2EBDD';
  const sevMap = {
    low: '#7D95C4', medium: '#C4A275', high: '#E87A2A', critical: '#D67373',
  };
  return (
    <div
      className="border border-border rounded overflow-hidden"
      style={{ background: err.resolved ? '#0F0D0B88' : '#1A1613' }}
      data-testid={`error-row-${err.error_id}`}
    >
      <div className="flex items-start gap-2 p-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          data-testid={`error-select-${err.error_id}`}
          className="mt-1"
          disabled={err.resolved}
        />
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onExpand}>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span style={{
              padding: '1px 6px', borderRadius: 3, background: src.bg, color: src.fg,
              fontSize: 9, fontWeight: 900, letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace",
              textTransform: 'uppercase',
            }}>{err.source}</span>
            <span style={{ color: lvl, fontSize: 10, fontWeight: 800,
                           fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>
              {err.level?.toUpperCase()}
            </span>
            {err.severity && (
              <span style={{
                padding: '1px 6px', borderRadius: 3, border: `1px solid ${sevMap[err.severity]}`,
                color: sevMap[err.severity], fontSize: 9, fontWeight: 900, letterSpacing: 1,
                fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase',
              }} data-testid={`error-severity-${err.error_id}`}>
                {err.severity}
              </span>
            )}
            {err.category && (
              <span style={{ fontSize: 10, color: '#C4A275',
                              fontFamily: "'JetBrains Mono', monospace" }}>
                #{err.category}
              </span>
            )}
            {err.count > 1 && (
              <span style={{
                padding: '1px 6px', borderRadius: 10, background: '#E87A2A', color: '#0F0D0B',
                fontSize: 9, fontWeight: 900, letterSpacing: 0,
                fontFamily: "'JetBrains Mono', monospace",
              }}>×{err.count}</span>
            )}
            {err.ai_analysis && (
              <span title="Análise IA disponível" style={{
                fontSize: 12, color: '#E87A2A',
              }} data-testid={`error-ai-badge-${err.error_id}`}>🤖</span>
            )}
            {err.resolved && (
              <span style={{
                padding: '1px 6px', borderRadius: 3, border: '1px solid #5A7A3A',
                color: '#5A7A3A', fontSize: 9, fontWeight: 800, letterSpacing: 1,
                fontFamily: "'JetBrains Mono', monospace",
              }}>✓ RESOLVIDO</span>
            )}
            <span className="text-xs text-muted-foreground ml-auto"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {_fmtDate(err.created_at)}
            </span>
          </div>
          <div style={{
            fontSize: 12, color: '#F2EBDD', fontFamily: 'monospace',
            wordBreak: 'break-word',
          }}>{err.message}</div>
          {(err.url || err.http_status || err.user_email) && (
            <div className="text-xs text-muted-foreground mt-1"
                 style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {err.http_method && `${err.http_method} `}{err.url}
              {err.http_status && ` · ${err.http_status}`}
              {err.user_email && ` · 👤 ${err.user_email}`}
            </div>
          )}
        </div>
        {!err.ai_analysis && !err.resolved && (
          <Button
            size="sm" variant="ghost" onClick={onAnalyze} disabled={analyzing || busy}
            data-testid={`error-analyze-${err.error_id}`}
            title="Analisar com Gemini 3.1 Pro"
            style={{ fontSize: 10, letterSpacing: 1, color: '#E87A2A',
                     fontFamily: "'JetBrains Mono', monospace" }}
          >
            {analyzing ? '⏳' : '🤖 IA'}
          </Button>
        )}
        {!err.resolved && (
          <Button
            size="sm" variant="ghost" onClick={onResolve} disabled={busy}
            data-testid={`error-resolve-${err.error_id}`}
            title="Marcar como resolvido"
            style={{ fontSize: 10, letterSpacing: 1, color: '#5A7A3A',
                     fontFamily: "'JetBrains Mono', monospace" }}
          >
            ✓
          </Button>
        )}
      </div>
      {expanded && (
        <>
          {err.ai_analysis && (
            <div
              data-testid={`error-ai-analysis-${err.error_id}`}
              style={{
                background: '#151312', borderTop: '1px solid #E87A2A',
                padding: 12, color: '#F2EBDD',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              }}
            >
              <div style={{
                color: '#E87A2A', fontSize: 10, fontWeight: 900, letterSpacing: 2,
                marginBottom: 8, textTransform: 'uppercase',
              }}>
                🤖 Análise IA (Gemini 3.1 Pro)
                {err.ai_analysis.confidence && (
                  <span style={{ marginLeft: 8, color: '#C4A275' }}>
                    · confiança: {err.ai_analysis.confidence}
                  </span>
                )}
              </div>
              {err.ai_analysis.category_suggested && (
                <div style={{ marginBottom: 6 }}>
                  <span style={{ color: '#C4A275' }}>Categoria sugerida:</span>{' '}
                  <b>{err.ai_analysis.category_suggested}</b>
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <div style={{ color: '#C4A275', fontSize: 9, letterSpacing: 1,
                              textTransform: 'uppercase', marginBottom: 2 }}>
                  Causa raiz
                </div>
                <div style={{ fontFamily: 'sans-serif', lineHeight: 1.5 }}>
                  {err.ai_analysis.root_cause}
                </div>
              </div>
              <div>
                <div style={{ color: '#C4A275', fontSize: 9, letterSpacing: 1,
                              textTransform: 'uppercase', marginBottom: 2 }}>
                  Sugestão de fix
                </div>
                <div style={{ fontFamily: 'sans-serif', lineHeight: 1.5,
                              whiteSpace: 'pre-wrap' }}>
                  {err.ai_analysis.fix_suggestion}
                </div>
              </div>
            </div>
          )}
          {err.stack && (
            <pre
              data-testid={`error-stack-${err.error_id}`}
              style={{
                background: '#0F0D0B', color: '#C4A275',
                padding: 10, margin: 0, fontSize: 10, overflowX: 'auto',
                borderTop: '1px solid #3D362E',
                fontFamily: "'JetBrains Mono', monospace",
                whiteSpace: 'pre-wrap',
              }}
            >
{err.stack}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
